import {useEffect} from 'react'

import {useNonReactiveCallback} from '#/lib/hooks/useNonReactiveCallback'

/**
 * The page a reader is on, told outwards once per *page*.
 *
 * The fullscreen reader hands its caller the page it has come to rest on, because that is
 * how a read knows where it stopped: no submission can hold it — a page that asks nothing
 * is answered by nothing, and most of a document asks nothing — so the reader's *path* is
 * the only record of it (`#/lib/document/report`).
 *
 * The one thing this must never do is tell it twice. A report is a *write* at the far end:
 * an entry in a read's path carries when the page was reached, so a second report of a page
 * that has not changed is a second entry rather than a no-op. And the surface that owns the
 * read is a live feed card whose write re-renders it — which is how the obvious spelling of
 * this effect becomes a loop:
 *
 *     useEffect(() => {
 *       onPageSeen?.(page)
 *     }, [onPageSeen, page])
 *
 * `onPageSeen` is usually an arrow written in the card's own body (`page => record(page)`),
 * so it is a *new function* on every render of the card: the effect fires again for the same
 * page, the record changes, the card renders, and round it goes. React either refuses to
 * render at all (\"Maximum update depth exceeded\"), or — with a write that only sometimes
 * changes what it writes — quietly records the same page a dozen times before it settles.
 *
 * The identity that matters is the *reader's*: the page. `useNonReactiveCallback` (this
 * repo's tool for exactly this) keeps the caller's latest function reachable while giving it
 * one stable identity, so the report fires when the page changes and for nothing else. A
 * caller that hands over a different function because *it* re-rendered is not news about the
 * page, and is not treated as any.
 *
 * Firing on mount is deliberate rather than an oversight: the page a reader opened on is the
 * first page of their path, which is why a card can offer *carry on from here* after a read
 * that only ever pressed Play.
 */
export function usePageReport(
  /** The page the reader is on. */
  page: number,
  /** Where it is told: the read's own record, which is older than this reader and outlives it. */
  report?: (page: number) => void,
): void {
  const tell = useNonReactiveCallback(report)
  useEffect(() => {
    tell(page)
  }, [page, tell])
}
